import { action, computed } from "@ember/object";
import { service } from "@ember/service";
import { isEmpty } from "@ember/utils";
import {
  attributeBindings,
  classNameBindings,
  classNames,
} from "@ember-decorators/component";
import { bind } from "discourse/lib/decorators";
import { makeArray } from "discourse/lib/helpers";
import MultiSelectComponent from "discourse/select-kit/components/multi-select";
import {
  pluginApiIdentifiers,
  selectKitOptions,
} from "discourse/select-kit/components/select-kit";
import { i18n } from "discourse-i18n";
import SelectKitRow from "./select-kit/select-kit-row";
import TagRow from "./tag-row";

@attributeBindings("selectKit.options.categoryId:category-id")
@classNames("mini-tag-chooser")
@classNameBindings("noTags")
@selectKitOptions({
  allowAny: "allowAnyTag",
  fullWidthOnMobile: true,
  filterable: true,
  caretDownIcon: "caretIcon",
  caretUpIcon: "caretIcon",
  termMatchesForbidden: false,
  categoryId: null,
  everyTag: false,
  closeOnChange: false,
  maximum: "maxTagsPerTopic",
  autoInsertNoneItem: false,
  useHeaderFilter: false,
  valueProperty: "id",
  nameProperty: "name",
})
@pluginApiIdentifiers(["mini-tag-chooser"])
export default class MiniTagChooser extends MultiSelectComponent {
  @service tagUtils;

  valueProperty = "id";
  nameProperty = "name";

  @computed("value.length")
  get noTags() {
    return isEmpty(this.value);
  }

  @computed("allowCreate", "site.can_create_tag")
  get allowAnyTag() {
    return this.allowCreate || this.site?.can_create_tag;
  }

  @computed("siteSettings.max_tag_search_results")
  get maxTagSearchResults() {
    return this.siteSettings.max_tag_search_results;
  }

  @computed("siteSettings.max_tags_per_topic")
  get maxTagsPerTopic() {
    return this.siteSettings.max_tags_per_topic;
  }

  @computed("value.[]")
  get tags() {
    return makeArray(this.value);
  }

  modifyComponentForRow(collection, item) {
    if (typeof item?.onSelect === "function") {
      return SelectKitRow;
    }

    if (this.getValue(item) === this.selectKit.filter && !item.count) {
      return SelectKitRow;
    }

    return TagRow;
  }

  modifyNoSelection() {
    if (this.selectKit.options.minimum > 0) {
      return this.defaultItem(
        null,
        i18n("tagging.choose_for_topic_required", {
          count: this.selectKit.options.minimum,
        })
      );
    } else {
      return this.defaultItem(null, i18n("tagging.choose_for_topic"));
    }
  }

  @computed("value.[]", "content.[]")
  get caretIcon() {
    const maximum = this.selectKit.options.maximum;
    return maximum && makeArray(this.value).length >= parseInt(maximum, 10)
      ? null
      : "plus";
  }

  @computed("tags.[]")
  get content() {
    let tags = this.tags;
    if (this.selectKit.options.hiddenValues) {
      tags = tags.filter((t) => {
        const id = typeof t === "object" ? t.id : t;
        return !this.selectKit.options.hiddenValues.some((h) => {
          const hiddenId = typeof h === "object" ? h.id : h;
          return hiddenId === id;
        });
      });
    }
    return tags.map((t) => {
      if (typeof t === "object" && t !== null) {
        return this.defaultItem(t.id, t.name);
      }
      return this.defaultItem(t, t);
    });
  }

  @action
  _onChange(value, items) {
    if (this.onChange) {
      this.onChange(items);
    } else {
      this.set("value", items);
    }
  }

  validateCreate(filter, content) {
    const selectedTagNames = (this.selectedContent || []).map((t) =>
      typeof t === "string" ? t : t.name
    );

    return this.tagUtils.validateCreate(
      filter,
      content,
      this.selectKit.options.maximum,
      (e) => this.addError(e),
      this.termMatchesForbidden,
      (value) => this.getValue(value),
      selectedTagNames
    );
  }

  createContentFromInput(input) {
    return this.tagUtils.createContentFromInput(input);
  }

  search(filter) {
    const maximum = this.selectKit.options.maximum;
    if (maximum === 0) {
      const key = "select_kit.max_content_reached";
      this.addError(i18n(key, { count: maximum }));
      return [];
    }

    const data = {
      q: filter || "",
      limit: this.maxTagSearchResults,
      categoryId: this.selectKit.options.categoryId,
    };

    if (this.value?.length) {
      const ids = this.value.map((v) => v.id);
      if (ids.length) {
        data.selected_tag_ids = ids.slice(0, 100);
      }
    }

    if (!this.selectKit.options.everyTag) {
      data.filterForInput = true;
    }

    return this.tagUtils.searchTags(
      "/tags/filter/search",
      data,
      this._transformJson
    );
  }

  @bind
  _transformJson(json) {
    if (this.isDestroyed || this.isDestroying) {
      return [];
    }

    this.setProperties({
      termMatchesForbidden: json.forbidden ? true : false,
      termMatchErrorMessage: json.forbidden_message,
    });

    let results = this.tagUtils.sortSearchResults(json.results);

    if (json.required_tag_group) {
      this.set(
        "selectKit.options.translatedFilterPlaceholder",
        i18n("tagging.choose_for_topic_required_group", {
          count: json.required_tag_group.min_count,
          name: json.required_tag_group.name,
        })
      );
    } else {
      this.set("selectKit.options.translatedFilterPlaceholder", null);
    }

    const selectedNames = this.tags.map((t) =>
      typeof t === "string" ? t : t.name
    );
    return results.filter((r) => !selectedNames.includes(r.name));
  }
}
